import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccountRole } from '../context/RoleContext';
import ConfirmModal from './ConfirmModal';

/**
 * Global popup for the one case useAuthNavigate blocks: an employer account tapping
 * "Ярилцлага өгөх" (or any other /chat link). Used to be a plain toast telling them to
 * switch role by hand and try again; this offers the switch and completes the tap in
 * one step instead — the same pattern CandidateSearchDemo's role-switch confirm already
 * uses for the mirror case (a jobseeker tapping an employer-only action).
 *
 * A window event, not a prop or context call, because useAuthNavigate is called from
 * everywhere a chat button lives — the job page, the feed, the map, saved jobs, the
 * preview drawer — and a global listener mounted once is the only way to reach all of
 * them from a single modal instance.
 */
const RoleSwitchGuard = () => {
  const navigate = useNavigate();
  const { setAccountRole } = useAccountRole();
  const [isOpen, setIsOpen] = useState(false);
  // Where the tap wanted to go, so confirming completes it instead of just flipping
  // the role and leaving the employer to tap "Ярилцлага өгөх" a second time.
  const redirectRef = useRef(null);
  // Why the switch is needed, when the blocked action is not the interview.
  const [subtitle, setSubtitle] = useState(null);

  useEffect(() => {
    const handler = (event) => {
      redirectRef.current = event.detail?.redirectTo || null;
      setSubtitle(event.detail?.subtitle || null);
      setIsOpen(true);
    };
    window.addEventListener('role:employerBlocked', handler);
    return () => window.removeEventListener('role:employerBlocked', handler);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    redirectRef.current = null;
  }, []);

  const confirm = useCallback(() => {
    setAccountRole('jobseeker');
    const redirectTo = redirectRef.current;
    redirectRef.current = null;
    if (redirectTo) navigate(redirectTo);
  }, [setAccountRole, navigate]);

  return (
    <ConfirmModal
      isOpen={isOpen}
      onClose={close}
      onConfirm={confirm}
      title="Ажил горилогч болох уу?"
      subtitle={subtitle || "Ярилцлагад орохын тулд ажил горилогч байх шаардлагатай."}
    />
  );
};

export default RoleSwitchGuard;
